import { Type, plainToInstance } from 'class-transformer'
import { Entity } from '@core/entities/entity'
import { User } from '@core/entities/user'
import { ISiteBoxDocumentConfigConfig } from '@core/entities/SiteBoxDocumentConfig'
import { SiteBoxDocumentConfigResult } from '@core/entities/SiteBoxDocumentConfig/SiteBoxDocumentConfigResult'
import { IDocumentIntelligence } from './IDocumentIntelligence'
import { DocumentIntelligenceStatus } from './types'
import moment from 'moment'
import { localizedStrings } from '@core/strings'

export class DocumentIntelligence extends Entity<IDocumentIntelligence> {
  config: ISiteBoxDocumentConfigConfig

  @Type(() => SiteBoxDocumentConfigResult)
  result: SiteBoxDocumentConfigResult[]

  status: DocumentIntelligenceStatus

  reviewDate?: Date

  @Type(() => User)
  reviewer?: User | null

  expiryDate?: string

  static readonly EXPIRY_AMBER_THRESHOLD_DAYS = 7

  public static new(payload: IDocumentIntelligence) {
    const entity = plainToInstance(DocumentIntelligence, payload)

    return entity
  }

  public static getStatusBorderColor(
    status: DocumentIntelligenceStatus,
  ): string {
    switch (status) {
      case 'PASS':
        return 'success.main'
      case 'FAIL':
        return 'error.main'
      case 'IN_REVIEW':
        return 'warning.main'
      default:
        return 'divider'
    }
  }

  public static isExpiryDateInFuture(date: string | null | undefined): boolean {
    return !!date && moment(date).isAfter(moment(), 'day')
  }

  public getReviewStatusTextColor(): string | null {
    if (this.status === 'PASS') {
      return 'primary.main'
    }
    if (this.status === 'FAIL') {
      return 'error.main'
    }
    return null
  }

  public hasExpiry(): boolean {
    return !!this.expiryDate
  }

  public isExpired(): boolean {
    if (!this.expiryDate) {
      return false
    }
    return moment(this.expiryDate).isBefore(moment())
  }

  public getDaysUntilExpiry(): number | null {
    if (!this.expiryDate) {
      return null
    }
    return moment(this.expiryDate).diff(moment(), 'days')
  }

  public getExpiryStatusTextColor():
    | 'success.main'
    | 'warning.main'
    | 'error.main'
    | null {
    if (!this.expiryDate || this.status !== 'PASS') {
      return null
    }
    if (this.isExpired()) {
      return 'error.main'
    }
    const days = this.getDaysUntilExpiry() ?? 0
    if (days <= DocumentIntelligence.EXPIRY_AMBER_THRESHOLD_DAYS) {
      return 'warning.main'
    }
    return 'success.main'
  }

  public getFormattedExpiryDate(format: string = 'DD/MM/YYYY'): string | null {
    if (!this.expiryDate) {
      return null
    }
    return moment(this.expiryDate).format(format)
  }

  public getExpiryReviewLabel(): string | null {
    if (!this.expiryDate || this.status !== 'PASS') {
      return null
    }
    const formatted = this.getFormattedExpiryDate()
    const prefix = this.isExpired()
      ? localizedStrings.expired
      : localizedStrings.expires
    return `${prefix} ${formatted}`
  }

  public getExpirySelectorSuffix(): string | null {
    if (this.status === 'PASS') {
      if (!this.expiryDate) {
        return localizedStrings.passed
      }
      const formatted = this.getFormattedExpiryDate()
      const verb = this.isExpired()
        ? localizedStrings.expired
        : localizedStrings.expires
      return `${localizedStrings.passed} — ${verb} ${formatted}`
    }
    if (this.status === 'FAIL') {
      return localizedStrings.failed
    }
    return null
  }
}
